// src/redux/slices/dashboardSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_ROUTES from "../../config/apiRoutes";

// Fetch dashboard statistics
export const fetchDashboardStatistics = createAsyncThunk(
  'dashboard/fetchStatistics',
  async ({ from, to } = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    
    // Default to current year if no dates provided
    const currentYear = new Date().getFullYear();
    const fromDate = from || `${currentYear}-01-01T00:00:00.000Z`;
    const toDate = to || `${currentYear}-12-31T23:59:59.999Z`;
    
    try {
      const response = await axios.get(
        API_ROUTES.DASHBOARD.GET_STATISTICS(fromDate, toDate),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Dashboard Statistics API Error:', error);
      return rejectWithValue(error.response?.data?.message || "Failed to fetch dashboard statistics");
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  users: {
    totalUsers: 0,
    totalVerified: 0,
    totalCompanyUsers: 0,
    totalPersonalUsers: 0,
    totalDeviceUsers: {
      Android: 0,
      iOS: 0
    }
  },
  auctions: {
    totalAuctions: 0,
    totalScheduledAuctions: 0,
    totalActiveAuctions: 0,
    totalLiveAuctions: 0,
    totalNoshowAuctions: 0,
    totalCompletedAuctions: 0,
    totalDraftAuctions: 0,
    totalCancelledAuctions: 0,
    totalBlockedAuctions: 0,
    totalDeletedAuctions: 0
  },
  clips: {
    totalClips: 0,
    totalActiveClips: 0,
    totalArchivedClips: 0,
    totalDeletedClips: 0,
    totalBlockedClips: 0
  },
  orders: {
    totalOrders: 0,
    totalDeliveryPending: 0,
    totalDeliveryShipped: 0,
    totalDeliveryDelivered: 0,
    totalDeliveryCancelled: 0,
    totalDeliveryReturned: 0,
    totalOrderPending: 0,
    totalOrderProcessing: 0,
    totalOrderCompleted: 0,
    totalOrderPaidout: 0,
    totalOrderCancelled: 0,
    totalOrderRefunded: 0
  },
  withdrawls: {
    totalWithdrawls: 0,
    totalPendingWithdrawls: 0,
    totalProcessingWithdrawls: 0,
    totalCompletedWithdrawls: 0,
    totalCompanyWithdrawls: 0,
    totalPersonalWithdrawls: 0
  },
  subscriptionPlans: {
    totalSubscriptionPlans: 0
  },
  admins: {
    totalAdmins: 0,
    totalAdminsByPermissions: {}
  },
  meydanStats: {
    totalProfit: 0,
    totalProfitBySubscriptions: 0,
    totalProfitByClips: 0,
    totalProfitByAuctions: 0
  },
  bids: []
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users || initialState.users;
        state.auctions = action.payload.auctions || initialState.auctions;
        state.clips = action.payload.clips || initialState.clips;
        state.orders = action.payload.orders || initialState.orders;
        state.withdrawls = action.payload.withdrawls || initialState.withdrawls;
        state.subscriptionPlans = action.payload.subscriptionPlans || initialState.subscriptionPlans;
        state.admins = action.payload.admins || initialState.admins;
        state.meydanStats = action.payload.meydanStats || initialState.meydanStats;
        state.bids = action.payload.bids || [];
      })
      .addCase(fetchDashboardStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
